import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Search, Download, Filter, Calendar as CalendarIcon, User, Activity, FileText, Settings, Trash2, Edit, Eye, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useUsersList } from '@/hooks/useUsersData';

type ActivityType = 
  | 'user_login'
  | 'user_logout'
  | 'user_created'
  | 'user_updated'
  | 'user_deleted'
  | 'role_changed'
  | 'permission_changed'
  | 'settings_updated'
  | 'data_exported'
  | 'data_imported'
  | 'record_created'
  | 'record_updated'
  | 'record_deleted'
  | 'record_viewed';

interface ActivityLog {
  id: string;
  timestamp: string;
  userId: string;
  userName: string;
  userEmail: string;
  action: ActivityType;
  description: string;
  ipAddress: string;
  userAgent: string;
  resource?: string;
  resourceId?: string;
  changes?: Record<string, { old: string; new: string }>;
}

const MOCK_ACTIVITY_LOGS: ActivityLog[] = [
  {
    id: '1',
    timestamp: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
    userId: '1',
    userName: 'John Smith',
    userEmail: 'john.smith@company.com',
    action: 'user_login',
    description: 'User logged in successfully',
    ipAddress: '192.168.1.100',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/120.0.0.0',
  },
  {
    id: '2',
    timestamp: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
    userId: '2',
    userName: 'Sarah Johnson',
    userEmail: 'sarah.johnson@company.com',
    action: 'user_created',
    description: 'Created new user account for Michael Brown',
    ipAddress: '192.168.1.101',
    userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) Safari/605.1.15',
    resource: 'User',
    resourceId: '5',
  },
  {
    id: '3',
    timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
    userId: '1',
    userName: 'John Smith',
    userEmail: 'john.smith@company.com',
    action: 'settings_updated',
    description: 'Updated company settings',
    ipAddress: '192.168.1.100',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/120.0.0.0',
    resource: 'Settings',
    resourceId: 'company',
    changes: {
      companyName: { old: 'Acme Corp', new: 'Acme Corporation' },
      timezone: { old: 'UTC', new: 'America/New_York' },
    },
  },
  {
    id: '4',
    timestamp: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
    userId: '3',
    userName: 'Emily Davis',
    userEmail: 'emily.davis@company.com',
    action: 'role_changed',
    description: 'Changed role for Michael Brown from User to Administrator',
    ipAddress: '192.168.1.102',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) Edge/120.0.0.0',
    resource: 'User',
    resourceId: '5',
    changes: {
      role: { old: 'user', new: 'administrator' },
    },
  },
  {
    id: '5',
    timestamp: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
    userId: '2',
    userName: 'Sarah Johnson',
    userEmail: 'sarah.johnson@company.com',
    action: 'data_exported',
    description: 'Exported user activity log',
    ipAddress: '192.168.1.101',
    userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) Safari/605.1.15',
    resource: 'ActivityLog',
  },
  {
    id: '6',
    timestamp: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
    userId: '1',
    userName: 'John Smith',
    userEmail: 'john.smith@company.com',
    action: 'record_updated',
    description: 'Updated customer record for ABC Industries',
    ipAddress: '192.168.1.100',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/120.0.0.0',
    resource: 'Customer',
    resourceId: 'cust-123',
  },
  {
    id: '7',
    timestamp: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
    userId: '4',
    userName: 'Michael Brown',
    userEmail: 'michael.brown@company.com',
    action: 'user_login',
    description: 'User logged in successfully',
    ipAddress: '192.168.1.103',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) Firefox/121.0',
  },
  {
    id: '8',
    timestamp: new Date(Date.now() - 1000 * 60 * 150).toISOString(),
    userId: '2',
    userName: 'Sarah Johnson',
    userEmail: 'sarah.johnson@company.com',
    action: 'permission_changed',
    description: 'Updated permissions for User role',
    ipAddress: '192.168.1.101',
    userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) Safari/605.1.15',
    resource: 'Role',
    resourceId: 'user',
  },
];

const ACTION_LABELS: Record<ActivityType, string> = {
  user_login: 'User Login',
  user_logout: 'User Logout',
  user_created: 'User Created',
  user_updated: 'User Updated',
  user_deleted: 'User Deleted',
  role_changed: 'Role Changed',
  permission_changed: 'Permission Changed',
  settings_updated: 'Settings Updated',
  data_exported: 'Data Exported',
  data_imported: 'Data Imported',
  record_created: 'Record Created',
  record_updated: 'Record Updated',
  record_deleted: 'Record Deleted',
  record_viewed: 'Record Viewed',
};

const ACTION_ICONS: Record<ActivityType, React.ComponentType<{ className?: string }>> = {
  user_login: User,
  user_logout: User,
  user_created: Plus,
  user_updated: Edit,
  user_deleted: Trash2,
  role_changed: Settings,
  permission_changed: Settings,
  settings_updated: Settings,
  data_exported: Download,
  data_imported: Download,
  record_created: Plus,
  record_updated: Edit,
  record_deleted: Trash2,
  record_viewed: Eye,
};

const ACTION_COLORS: Record<ActivityType, string> = {
  user_login: 'text-blue-600 bg-blue-50',
  user_logout: 'text-gray-600 bg-gray-50',
  user_created: 'text-green-600 bg-green-50',
  user_updated: 'text-yellow-600 bg-yellow-50',
  user_deleted: 'text-red-600 bg-red-50',
  role_changed: 'text-purple-600 bg-purple-50',
  permission_changed: 'text-purple-600 bg-purple-50',
  settings_updated: 'text-orange-600 bg-orange-50',
  data_exported: 'text-indigo-600 bg-indigo-50',
  data_imported: 'text-indigo-600 bg-indigo-50',
  record_created: 'text-green-600 bg-green-50',
  record_updated: 'text-yellow-600 bg-yellow-50',
  record_deleted: 'text-red-600 bg-red-50',
  record_viewed: 'text-blue-600 bg-blue-50',
};

export function UserActivityLogPage() {
  const { data: users = [] } = useUsersList();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const [selectedAction, setSelectedAction] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [activities] = useState<ActivityLog[]>(MOCK_ACTIVITY_LOGS);

  const filteredActivities = useMemo(() => {
    let filtered = [...activities];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (activity) =>
          activity.userName.toLowerCase().includes(query) ||
          activity.userEmail.toLowerCase().includes(query) ||
          activity.description.toLowerCase().includes(query) ||
          activity.ipAddress.includes(query)
      );
    }

    // User filter
    if (selectedUser !== 'all') {
      filtered = filtered.filter((activity) => activity.userId === selectedUser);
    }

    // Action filter
    if (selectedAction !== 'all') {
      filtered = filtered.filter((activity) => activity.action === selectedAction);
    }

    // Date range filter
    if (dateFrom) {
      filtered = filtered.filter(
        (activity) => new Date(activity.timestamp) >= dateFrom
      );
    }
    if (dateTo) {
      const endOfDay = new Date(dateTo);
      endOfDay.setHours(23, 59, 59, 999);
      filtered = filtered.filter(
        (activity) => new Date(activity.timestamp) <= endOfDay
      );
    }

    // Sort by timestamp (newest first)
    filtered.sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    return filtered;
  }, [activities, searchQuery, selectedUser, selectedAction, dateFrom, dateTo]);

  const handleExport = () => {
    toast.success('Activity log exported successfully');
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedUser('all');
    setSelectedAction('all');
    setDateFrom(undefined);
    setDateTo(undefined);
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return format(date, 'MMM d, yyyy HH:mm');
  };

  return (
    <div className="p-6">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">User Activity Log</h1>
          <p className="text-muted-foreground mt-2">
            Audit trail of user actions and system events for compliance and security
          </p>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by user, description, or IP address..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>

                {/* User Filter */}
                <Select value={selectedUser} onValueChange={setSelectedUser}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="All Users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.firstName} {user.lastName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Action Filter */}
                <Select value={selectedAction} onValueChange={setSelectedAction}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="All Actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    {Object.entries(ACTION_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-4">
                {/* Date From */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-[200px] justify-start text-left font-normal',
                        !dateFrom && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateFrom ? format(dateFrom, 'MMM d, yyyy') : 'From date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dateFrom}
                      onSelect={setDateFrom}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                {/* Date To */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-[200px] justify-start text-left font-normal',
                        !dateTo && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateTo ? format(dateTo, 'MMM d, yyyy') : 'To date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dateTo}
                      onSelect={setDateTo}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Button variant="outline" onClick={handleClearFilters}>
                  <Filter className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>

                <div className="flex-1" />

                <Button onClick={handleExport}>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Count */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {filteredActivities.length} of {activities.length} activities
          </p>
        </div>

        {/* Activity Log Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Timestamp</TableHead>
                  <TableHead className="w-[140px]">Action</TableHead>
                  <TableHead className="w-[200px]">User</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-[140px]">IP Address</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredActivities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12">
                      <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No activities found</h3>
                      <p className="text-muted-foreground">
                        Try adjusting your filters to see more results
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredActivities.map((activity) => {
                    const Icon = ACTION_ICONS[activity.action];
                    return (
                      <TableRow key={activity.id}>
                        <TableCell className="font-mono text-sm">
                          {formatTimestamp(activity.timestamp)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={cn('p-1.5 rounded', ACTION_COLORS[activity.action])}>
                              <Icon className="w-3.5 h-3.5" />
                            </div>
                            <span className="text-sm font-medium">
                              {ACTION_LABELS[activity.action]}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            <p className="text-sm font-medium">{activity.userName}</p>
                            <p className="text-xs text-muted-foreground">{activity.userEmail}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="text-sm">{activity.description}</p>
                            {activity.resource && (
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  {activity.resource}
                                  {activity.resourceId && `: ${activity.resourceId}`}
                                </Badge>
                              </div>
                            )}
                            {activity.changes && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {Object.entries(activity.changes).map(([key, value]) => (
                                  <div key={key}>
                                    {key}: <span className="line-through">{value.old}</span> →{' '}
                                    <span className="font-medium">{value.new}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {activity.ipAddress}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
